"use client";

import { useEffect, useRef } from "react";
import { APP_VERSION } from "../lib/version";
import { hasUnsavedWork, subscribeUnsavedWork } from "../lib/unsaved-work";

// Silent force-upgrade for cached / installed (home-screen, desktop PWA) clients.
//
// Each build bakes in APP_VERSION. This polls a no-store /api/version (served by
// whatever deployment is currently live) on load, tab-focus, visibility, and a
// 2-minute interval. When the live release is newer than the one this client is
// running, it clears caches + updates the service worker and hard-reloads onto the
// new build — no prompt. A per-target sessionStorage flag prevents reload loops.
//
// It also reloads when the DEPLOYMENT changes under a stable URL at the same release — the
// staging-link case, where a branch is redeployed to the same address many times a day and the
// release number never moves. Same protections: unsaved work and holdOnPaths still defer it.
//
// `holdOnPaths` lets a route defer the refresh while a destructive reload would
// lose work in progress (e.g. the customer checkout / payment page) — the upgrade
// just happens on the next check once the user has navigated away.
//
// Unsaved-work registry (see lib/unsaved-work): a per-path hold is too blunt for the
// staff app, where operators sit on /schedule all day — holding that path would stop
// them ever upgrading, defeating force-upgrade. Instead any sheet that holds typed
// input registers a token WHILE it is dirty; the upgrade is skipped while a token is
// held and fires the moment the last one clears (the subscription re-checks at once,
// so there is no wait for the next poll).
export function VersionGuard({
  endpoint = "/api/version",
  holdOnPaths = [],
  intervalMs = 120_000,
}: {
  endpoint?: string;
  holdOnPaths?: string[];
  intervalMs?: number;
}) {
  const busy = useRef(false);
  // The deployment this tab loaded from, learned on the first poll. A release number only moves on
  // a production release, so on a staging link — one stable URL re-pointed at a new build every
  // time the branch moves — it can never say "the build under you changed". This can.
  const build = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    /** Returns true when the reload was started; false when it was deferred (caller must retry). */
    async function upgrade(live: number, reason: string): Promise<boolean> {
      // Don't yank the page out from under work in progress.
      if (holdOnPaths.some((p) => location.pathname.startsWith(p))) return false;
      // ...or while a sheet holds unsaved typed input. Return BEFORE the "attempted"
      // flag below so the target version is NOT marked consumed — the next check()
      // (fired by the subscription when the work clears, or the poll) runs upgrade()
      // again and this time proceeds.
      if (hasUnsavedWork()) return false;
      // Attempt a given target at most once per tab session → no loops. Keyed on the reason as
      // well as the release, so a redeploy of the SAME release still gets its own attempt.
      const key = `aluume-upgrade:${live}:${reason}`;
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, "1");
      try {
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.update().catch(() => {})));
        }
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {
        // best effort — reload regardless
      }
      location.reload();
      return true;
    }

    async function check() {
      if (cancelled || busy.current || !navigator.onLine) return;
      busy.current = true;
      try {
        const res = await fetch(endpoint, { cache: "no-store" });
        if (!res.ok) return;
        const body = (await res.json()) as { version?: unknown; build?: unknown };
        const live = Number(body?.version);
        const liveBuild = typeof body?.build === "string" ? body.build : null;

        // A NEWER RELEASE is the production case: this client is behind what is live.
        if (Number.isFinite(live) && live > APP_VERSION) {
          await upgrade(live, "release");
          return;
        }

        // A DIFFERENT DEPLOYMENT at the same release is the staging case: the link is the same,
        // the build behind it is not. Only act on a value we have seen change — the first poll
        // just records where this tab came from.
        if (liveBuild && liveBuild !== "local") {
          // Adopt the new build as this tab's baseline ONLY when the reload actually goes ahead.
          // upgrade() defers while a sheet holds unsaved work or the path is held; adopting it
          // anyway would make the retry (fired the moment that clears) compare equal, and the tab
          // would sit on the old build for ever — the exact staleness this is here to prevent.
          if (build.current == null) build.current = liveBuild;
          else if (build.current !== liveBuild && (await upgrade(live, liveBuild))) {
            build.current = liveBuild;
          }
        }
      } catch {
        // offline / transient — ignore, try again next tick
      } finally {
        busy.current = false;
      }
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    const timer = setInterval(check, intervalMs);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    // Re-check the instant the last unsaved-work token clears, so a held upgrade fires
    // as soon as the sheet is closed rather than at the next poll tick.
    const unsubscribe = subscribeUnsavedWork(check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe();
    };
  }, [endpoint, intervalMs, holdOnPaths]);

  return null;
}
